import { useState } from 'react';
import type { SettleDestinationResponse, TransferChainNode, TransferDestination } from '@cuito/types';
import { Button } from '@cuito/ui';
import { stockApi, type TransferView } from '../../api/stock';
import { LOAD_FAILED_TEXT, settlementErrorText } from './settlementLabels';

/**
 * 剩余库存去向（契约 §9.4；UI 规范 554 行的 `选择库存去向` 按钮）。
 *
 * ## 数量**不由前端给**
 *
 * 子单的每一行 = 该展会展位上 `(productId, ownerTenantId)` 的 `StockItem.availableQty` **现值**
 * （服务端算）。因此请求体只有「去向 + 目标」—— 前端多传数量字段会被服务端当成不存在的输入，
 * 而「前端算出来的结转量」与真实剩余量必然对不上（这正是 A-P7-06 删掉 `event-carry-over` 的理由）。
 *
 * ## 去向未选之前不渲染目标控件
 *
 * `destination` 初值是**空**（两个单选都不选中）：去向是财务动作，默认替用户选一个
 * 会让「点开弹窗随手提交」落到一个没人确认过的去向上。选了一个才出现对应的目标控件
 * （`回仓` ⇒ 目标仓库；`结转下一场` ⇒ 下一场展会），两者互斥。
 *
 * ## 凭证链
 *
 * `查看凭证链`（`carry-over-chain`）取 §9.5 的**裸数组**并按**根在首位**渲染；
 * 每一环用属性标注（`data-chain-node` / `data-parent-transfer-id`）而不是新 testid
 * （冻结文档 §五 的 testid 是闭集；**待裁决 D-22**）。
 */
interface Props {
  /** 可选取向的调拨单（页面用 `GET /stock/transfers?status=RECEIVED` 取；**待裁决 D-7**） */
  transfers: TransferView[];
  warehouses: Array<{ id: string; name: string }>;
  events: Array<{ id: string; name: string }>;
  onClose: () => void;
  onDone: (result: SettleDestinationResponse) => void;
}

export default function CarryOverDialog({ transfers, warehouses, events, onClose, onDone }: Props) {
  const [transferId, setTransferId] = useState('');
  const [destination, setDestination] = useState<TransferDestination | ''>('');
  const [nextEventId, setNextEventId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [chain, setChain] = useState<TransferChainNode[] | null>(null);
  const [chainLoading, setChainLoading] = useState(false);

  const submit = async (): Promise<void> => {
    if (transferId === '' || destination === '' || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await stockApi.settleDestination(transferId, {
        destination,
        ...(destination === 'CARRY_TO_NEXT_EVENT'
          ? nextEventId === ''
            ? {}
            : { nextEventId }
          : warehouseId === ''
            ? {}
            : { warehouseId }),
      });
      onDone(result);
    } catch (e) {
      setError(settlementErrorText(e, LOAD_FAILED_TEXT));
    } finally {
      setSubmitting(false);
    }
  };

  const loadChain = async (): Promise<void> => {
    if (transferId === '') return;
    setChainLoading(true);
    setError(null);
    try {
      setChain(await stockApi.chain(transferId));
    } catch (e) {
      setChain(null);
      setError(settlementErrorText(e, LOAD_FAILED_TEXT));
    } finally {
      setChainLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        role="dialog"
        data-testid="carry-over-dialog"
        className="max-h-[90vh] w-[min(720px,92vw)] overflow-auto rounded-lg border-2 border-black bg-white p-5"
      >
        <h2 className="mb-4 text-xl font-bold">剩余库存去向</h2>

        <label className="mb-3 flex flex-col gap-1">
          <span className="text-sm font-bold">调拨单（已接收）</span>
          {/* 调拨单下拉**不挂 testid**（冻结表里没有它）⇒ 用 `data-field` 定位 */}
          <select
            data-field="transfer"
            value={transferId}
            onChange={(event) => {
              setTransferId(event.target.value);
              setChain(null);
            }}
            className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3"
          >
            <option value="">请选择调拨单</option>
            {transfers.map((transfer) => (
              <option key={transfer.id} value={transfer.id}>
                {transfer.code} · {transfer.targetLocation.name}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="mb-3 flex gap-4">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="carry-over-destination"
              data-testid="carry-over-destination-return"
              checked={destination === 'RETURN_TO_WAREHOUSE'}
              onChange={() => setDestination('RETURN_TO_WAREHOUSE')}
              className="h-4 w-4"
            />
            <span className="text-sm font-bold">回仓</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="carry-over-destination"
              data-testid="carry-over-destination-carry"
              checked={destination === 'CARRY_TO_NEXT_EVENT'}
              onChange={() => setDestination('CARRY_TO_NEXT_EVENT')}
              className="h-4 w-4"
            />
            <span className="text-sm font-bold">结转下一场</span>
          </label>
        </fieldset>

        {destination === 'CARRY_TO_NEXT_EVENT' && (
          <label className="mb-3 flex flex-col gap-1">
            <span className="text-sm font-bold">下一场展会</span>
            <select
              data-testid="carry-over-next-event"
              value={nextEventId}
              onChange={(event) => setNextEventId(event.target.value)}
              className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3"
            >
              <option value="">请选择下一场展会</option>
              {events.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {destination === 'RETURN_TO_WAREHOUSE' && (
          <label className="mb-3 flex flex-col gap-1">
            <span className="text-sm font-bold">目标仓库</span>
            <select
              data-testid="carry-over-warehouse"
              value={warehouseId}
              onChange={(event) => setWarehouseId(event.target.value)}
              className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3"
            >
              <option value="">请选择仓库</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </label>
        )}

        {error !== null && (
          <p
            data-testid="carry-over-error"
            className="mb-3 rounded-lg border-2 border-red-500 bg-red-50 p-2 text-sm text-red-600"
          >
            {error}
          </p>
        )}

        {chain !== null && (
          <ul className="mb-4 flex flex-col gap-1 text-sm" data-chain-list>
            {chain.map((node) => (
              <li
                key={node.id}
                data-chain-node
                data-parent-transfer-id={node.parentTransferId ?? ''}
                data-transfer-route={node.route}
                data-transfer-status={node.status}
                className="rounded border-2 border-black px-2 py-1"
              >
                {node.code} · {node.route} · {node.quantities.map((line) => `${line.quantity} 件`).join(' / ')}
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end gap-2">
          <Button data-testid="carry-over-submit" onClick={() => void submit()} disabled={submitting || transferId === '' || destination === ''}>
            确认去向
          </Button>
          <Button data-testid="carry-over-chain" variant="secondary" onClick={() => void loadChain()}>
            {chainLoading ? '加载中…' : '查看凭证链'}
          </Button>
          <Button variant="secondary" onClick={onClose}>
            取消
          </Button>
        </div>
      </div>
    </div>
  );
}
